import jwtDecode from 'jwt-decode';
import { isNull, isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import type { FC, ReactNode } from 'react';
import { createContext, useEffect, useReducer } from 'react';
import logger from 'src/utils/logger';
import { authApi } from '../api/authApi';
import { clientApi } from '../api/clientApi';
import { deviceApi } from '../api/deviceApi';
import { projectApi } from '../api/projectApi';
import { serviceClientApi } from '../api/serviceClientApi';
import { userApi } from '../api/userApi';
import type { User } from '../types/user';

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  is2FAEnabled: boolean;
  user: User | null;
}

interface AuthContextValue extends State {
  platform: 'JWT';
  login: (email: string, password: string, termsOfUseStatus: boolean) => Promise<void>;
  logout: () => Promise<void>;
  register: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    is2FAEnabled?: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: User;
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction;

const initialState: State = {
  isAuthenticated: false,
  is2FAEnabled: false,
  isInitialized: false,
  user: null
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, is2FAEnabled, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      is2FAEnabled,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      is2FAEnabled: user?.is_2fa_enabled,
      user
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    is2FAEnabled: false,
    user: null
  }),
  REGISTER: (state: State, action: RegisterAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      is2FAEnabled: user?.is_2fa_enabled,
      user
    };
  }
};

const reducer = (state: State, action: Action): State => (
  handlers[action.type] ? handlers[action.type](state, action) : state
);

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const currentTime = Date.now() / 1000;

  const decoded: any = jwtDecode(accessToken);

  return decoded.exp > currentTime;
};

const setSession = (data: any) => {
  if (!data) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  } else {
    const accessToken = data.access_token;
    const refreshToken = data.refresh_token;
    if (accessToken || refreshToken) {
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
    }
  }
};

const getAllRegisters = async (): Promise<void> => {
  try {
    // const registers1 = await deviceApi.getAllRegister1();
    // const register2 = await deviceApi.getAllRegister2();
    const allRegisters = await deviceApi?.getAllModbusRegisters();
    localStorage.setItem('registers', JSON.stringify(allRegisters));
    // const registers = [...registers1, ...register2];
    // localStorage.setItem('registers', JSON.stringify(registers));
  } catch (error) {
    logger('error', error);
  }
};

// eslint-disable-next-line consistent-return
const getPermissions = async (accessToken: string): Promise<void> => {
  try {
    const permissions: any = await userApi.getUserPermissions(accessToken);
    sessionStorage.setItem('permissions', JSON.stringify(permissions));
    return permissions?.permissions;
  } catch (error) {
    const locale = localStorage.getItem('locale');
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem('locale', locale);
    window.location.reload();
  }
};

const checkDefaultLanguageAndPartnerSettings = async () => {
  if (!localStorage.getItem('locale') || isUndefined(localStorage.getItem('locale')) || localStorage.getItem('locale') === 'undefined') {
    const hostName = window.location.hostname;
    let defaultLanguage = '';
    if (!isUndefined(process.env.REACT_APP_DEFAULT_LOCALE) && !isNull(process.env.REACT_APP_DEFAULT_LOCALE)) {
      defaultLanguage = process.env.REACT_APP_DEFAULT_LOCALE;
    } else if (hostName?.indexOf('baxi') !== -1) {
      defaultLanguage = 'en';
    } else if (hostName?.indexOf('fortes.it22.cloud') !== -1) {
      defaultLanguage = 'en';
    } else if (hostName?.indexOf('localhost') !== -1) {
      defaultLanguage = 'en';
    } else {
      defaultLanguage = 'nl';
    }
    localStorage.setItem('locale', defaultLanguage);
    window.location.reload();
  }

  if (!localStorage.getItem('partner_settings') || isUndefined(localStorage.getItem('partner_settings')) || localStorage.getItem('partner_settings') === 'undefined') {
    const partnerGlobalConfig: any = await userApi.getGlobalConfig();
    if (!isUndefined(partnerGlobalConfig?.data)) {
      localStorage.setItem('partner_settings', JSON.stringify(partnerGlobalConfig?.data));
    }
  }
};

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  checkDefaultLanguageAndPartnerSettings();

  useEffect(() => {
    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: false,
        is2FAEnabled: false,
        user: null,
      }
    });
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const refreshToken = window.localStorage.getItem('refreshToken');
        // if (!window.localStorage.getItem('registers') && isValidToken(accessToken)) {
        if (isValidToken(accessToken)) {
          getAllRegisters();
        }
        const userLocal: any = JSON.parse(window.localStorage.getItem('user'));

        const partnerGlobalConfig: any = await userApi.getGlobalConfig();

        const decodedObj: any = jwtDecode(accessToken);
        const userObj: any = await userApi.getByKeycloakId(decodedObj?.sub);
        localStorage.setItem('user', JSON.stringify(userObj));
        if (!isUndefined(partnerGlobalConfig?.data)) {
          localStorage.setItem('partner_settings', JSON.stringify(partnerGlobalConfig?.data));
        }

        if (!isUndefined(partnerGlobalConfig?.distributor_name)) {
          localStorage.setItem('partner_settings', JSON.stringify(partnerGlobalConfig));
        }

        if (isNull(localStorage.getItem('partner_settings')) || isUndefined(localStorage.getItem('partner_settings'))) {
          window.location.reload();
        }

        if (isValidToken(accessToken)) {
          // setInterval(async () => {
          //   await userApi.updateUserLastSeen();
          // }, 30 * 60 * 1000);

          const decoded: any = jwtDecode(accessToken);
          const roles = decoded?.realm_access?.roles;
          const isFortesUserRegex = new RegExp('^fortes_');
          const isFortesUser = roles?.find((role) => (
            isFortesUserRegex?.test(role)
          ));
          let userLocal2FAEnabled = isFortesUser?.length > 0 ? !userLocal?.is_qrverified : isUndefined(isFortesUser?.length) && userLocal.is_2fa_enabled && !userLocal?.is_qrverified;
          const permissions = await getPermissions(accessToken);
          const user: any = await authApi.me(accessToken, permissions, { is_2fa_enabled: userLocal2FAEnabled });
          if (localStorage.getItem('redirect_to') && !isUndefined(localStorage.getItem('redirect_to'))) {
            userLocal2FAEnabled = false;
          }

          if (process.env.REACT_APP_ENVIRONMENT !== 'PRODUCTION' && roles?.includes('client_admin')) {
            try {
              const clientData = JSON.parse(localStorage.getItem('client'));
              const clientId = clientData?.client_id;
              if (clientId) {
                const res: any = await projectApi.getProjectsWithMissingMainContact(clientId);
                const missing = res?.data ?? [];
                if (missing.length > 0) {
                  localStorage.setItem('projects_missing_contact', JSON.stringify(missing));
                } else {
                  localStorage.removeItem('projects_missing_contact');
                }
              }
            } catch (err) {
              logger('Missing contact check failed', err);
            }
          }

          await dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              is2FAEnabled: userLocal2FAEnabled,
              user
            }
          });
        } else if (window.location.pathname.indexOf('login') !== -1 && !accessToken) {
          logger('login');
        } else if (window.location.pathname.indexOf('forgot-password') !== -1) {
          logger('forgot-password');
        } else if (window.location.pathname.indexOf('reset-password') !== -1) {
          logger('reset-password');
        } else if (window.location.pathname.indexOf('/login') !== -1 && !accessToken) {
          window.location.href = '/login';
        } else if (refreshToken && !isValidToken(accessToken)) {
          try {
            const keycloakData = await authApi.refreshSession(refreshToken);
            if (keycloakData) {
              setSession(keycloakData);
              window.location.reload();
            }
          } catch (error) {
            const locale = localStorage.getItem('locale');
            localStorage.clear();
            localStorage.setItem('locale', locale);
            window.location.reload();
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              is2FAEnabled: false,
              user: null,
            }
          });
        }
      } catch (err) {
        logger('error', err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            is2FAEnabled: false,
            user: null,
          }
        });
      }
    };

    initialize();
  }, []);

  const updateUserLocalStorage = async (decoded, user, keycloakData, permissions) => {
    const loggedUser = await authApi.me(keycloakData.access_token, permissions, keycloakData);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.removeItem('missing_contact_last_prompted');
    const roles = decoded?.realm_access?.roles;
    const isClientUserRegex = new RegExp('^client_');
    const isServiceClientUserRegex = new RegExp('^service_client_');
    const isSubClientUserRegex = new RegExp('^sub_client_');
    const isClientUser = roles?.find((role) => (
      isClientUserRegex?.test(role)
    ));
    const isServiceClientUser = roles?.find((role) => (
      isServiceClientUserRegex?.test(role) || isSubClientUserRegex?.test(role)
    ));

    if (isClientUser?.length > 0) {
      const client: any = await clientApi.getById(+user?.client_id);
      localStorage.setItem('client', JSON.stringify(client?.data));
      localStorage.setItem('client_logo', client?.data?.logo);

      if (process.env.REACT_APP_ENVIRONMENT !== 'PRODUCTION' && roles?.includes('client_admin')) {
        try {
          const clientId = client?.data?.client_id;
          if (clientId) {
            const res: any = await projectApi.getProjectsWithMissingMainContact(clientId);
            const missing = res?.data ?? [];
            if (missing.length > 0) {
              localStorage.setItem('projects_missing_contact', JSON.stringify(missing));
            } else {
              localStorage.removeItem('projects_missing_contact');
            }
          }
        } catch (err) {
          logger('Missing contact check failed', err);
        }
      }
    } else if (isServiceClientUser?.length > 0) {
      const client: any = await serviceClientApi.getById(+user?.client_id);
      localStorage.setItem('client', JSON.stringify(client?.data));
      if (isServiceClientUser?.length > 0) {
        localStorage.setItem('client_logo', client?.data?.logo);
      }
    } else {
      localStorage.removeItem('client');
    }
    dispatch({
      type: 'LOGIN',
      payload: {
        user: loggedUser,
      }
    });
  };

  const login = async (email: string, password: string, termsOfUseStatus: boolean): Promise<void> => {
    const keycloakData: any = await authApi.login({ email, password });

    let permissions: any = await userApi.getUserPermissions(keycloakData.access_token);
    permissions = permissions?.permissions;
    sessionStorage.setItem('permissions', JSON.stringify(permissions));
    if (keycloakData) {
      setSession(keycloakData);
      const decoded: any = jwtDecode(keycloakData.access_token);
      const user: any = await userApi.getByKeycloakId(decoded?.sub);
      if (termsOfUseStatus) {
        await deviceApi.updateDocumentReviewStatus(user?.user_id, {
          termsAndConditionsDoc: true,
          privacyPolicyDoc: true
        });
      }

      const roles = decoded?.realm_access?.roles;
      const isFortesUserRegex = new RegExp('^fortes_');
      const isFortesUser = roles?.find((role) => (
        isFortesUserRegex?.test(role)
      ));

      user.is_2fa_enabled = keycloakData?.is_2fa_enabled;
      if (!user?.is_email_verified && user?.is_activated) {
        const locale = localStorage.getItem('locale');
        localStorage.clear();
        localStorage.setItem('locale', locale);
        localStorage.setItem('tempAccessToken', keycloakData.access_token);
        window.location.href = `/reset-password/${decoded?.sub}`;
      } else if (isFortesUser?.length > 0 && !user?.is_qrverified) {
        await updateUserLocalStorage(decoded, user, keycloakData, permissions);
        window.location.href = '/two-factor-authentication';
      } else if (isUndefined(isFortesUser?.length) && user.is_2fa_enabled && !user?.is_qrverified) {
        await updateUserLocalStorage(decoded, user, keycloakData, permissions);
        window.location.href = '/two-factor-authentication';
      } else if (user?.is_activated) {
        await updateUserLocalStorage(decoded, user, keycloakData, permissions);
      } else {
        const locale = localStorage.getItem('locale');
        localStorage.clear();
        localStorage.setItem('locale', locale);
        throw new Error('Account is deactivated');
      }
    }
  };

  const logout = async (): Promise<void> => {
    // const locale = localStorage.getItem('locale');
    const partnerSettings = localStorage.getItem('partner_settings');
    localStorage.clear();
    // localStorage.setItem('locale', locale);
    localStorage.setItem('partner_settings', partnerSettings);
    dispatch({ type: 'LOGOUT' });
    window.location.href = '/';
  };

  const register = async (): Promise<void> => {
    await authApi.register();
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
